import { FunctionComponent } from "react";

import {
  TagPicker as FabricTagPicker,
  FontWeights,
  IPickerItemProps,
  ITag,
  ITagItemSuggestionProps,
  mergeFuncStyles,
  TagItemSuggestion
} from "@bps/fluent-ui";

import {
  ConvertToWithDataSourceProps,
  ExtendedPickerProps
} from "./ExtendedPicker.types.ts";
import { extendPicker } from "./extendPicker.tsx";
import { MultiTagPickerItem } from "./MultiTagPickerItem.tsx";
import { MultiTagPickerItemProps } from "./MultiTagPickerItem.types.ts";
import { withStaticItems } from "./withStaticItems.tsx";

type WithOptional<T extends K, K extends object> = Omit<T, keyof K> &
  Partial<K>;

export type GenericMultiTagPickerProps<TItem> = ExtendedPickerProps<
  ITag,
  TItem
> & {
  /**
   * This can be used to override some TagItemSuggestion props
   */
  withTagItemSuggestionProps?: (item: TItem) => ITagItemSuggestionProps;
  /**
   * This can be used to override some TagItem props
   */
  withTagItemProps?: (
    item: TItem
  ) => WithOptional<MultiTagPickerItemProps, IPickerItemProps<ITag>>;
  loading?: boolean;
};

const ExtendedPickerBase = extendPicker<ITag, any>(FabricTagPicker);

/**
 * Generic TagPicker where the type of the item can be anything.
 * For this picker onResolveSuggestionItem and keyAccessor must be provided
 * @param props
 */
export const GenericMultiTagPicker = <TItem extends any>(
  props: GenericMultiTagPickerProps<TItem>
) => {
  // style differently suggestion items that are already picked
  const TypedExtendedPickerBase = ExtendedPickerBase as React.FunctionComponent<
    GenericMultiTagPickerProps<TItem>
  >;

  const onRenderSuggestionsItem: GenericMultiTagPickerProps<TItem>["onRenderSuggestionsItem"] =
    props.onRenderSuggestionsItem ??
    ((tag, itemProps, item) => {
      const isTagSelected = !!props.selectedKeys?.includes(tag.key as string);
      const otherProps = props.withTagItemSuggestionProps
        ? props.withTagItemSuggestionProps(item)
        : {};
      return (
        <TagItemSuggestion
          {...itemProps} // this line appears to be causing problems in version 8.122.7 of @fluentui/react
          {...otherProps}
          styles={mergeFuncStyles(
            ({ theme }) => ({
              suggestionTextOverflow: isTagSelected && {
                fontWeight: FontWeights.semibold,
                width: "100%",
                textAlign: "left",
                backgroundColor: theme.semanticColors.menuItemBackgroundChecked
              }
            }),
            otherProps.styles
          )}
        >
          {otherProps.children || tag.name}
        </TagItemSuggestion>
      );
    });

  const onRenderItem: GenericMultiTagPickerProps<TItem>["onRenderItem"] =
    props.onRenderItem ??
    ((itemProps, item) => {
      const mergedProps = props.withTagItemProps
        ? props.withTagItemProps(item)
        : {};
      return (
        <MultiTagPickerItem {...itemProps} {...mergedProps}>
          {mergedProps.children || itemProps.item.name}
        </MultiTagPickerItem>
      );
    });

  return (
    <TypedExtendedPickerBase
      {...props}
      onRenderSuggestionsItem={onRenderSuggestionsItem}
      onRenderItem={onRenderItem}
    />
  );
};

export type GenericStaticTagPickerProps<TItem> = ConvertToWithDataSourceProps<
  GenericMultiTagPickerProps<TItem>,
  TItem
>;

export const StaticExtendedPickerBase = withStaticItems<
  GenericMultiTagPickerProps<any>,
  ITag,
  any
>(GenericMultiTagPicker);

export type MultiTagPickerProps = Omit<
  GenericMultiTagPickerProps<ITag>,
  "onResolveSuggestionItem" | "keyAccessor"
>;

/**
 * Extended TagPicker (see extendTagPicker)
 * This picker can be used to resolve suggestions from the backend.
 * When the list of all items is resolved at once, StaticTagPicker is more convenient.
 * @param props
 */
export const MultiTagPicker: FunctionComponent<MultiTagPickerProps> = props => {
  return (
    <GenericMultiTagPicker
      {...props}
      onResolveSuggestionItem={x => x}
      keyAccessor={x => x.key as string}
    />
  );
};

/**
 * Generic StaticTagPicker where the type of the item can be anything.
 * For this picker onResolveSuggestionItem and keyAccessor must be provided
 * @param props
 */
export type StaticMultiTagPickerProps<TItem extends ITag = ITag> = Omit<
  GenericStaticTagPickerProps<TItem>,
  "onFilterItems" | "onResolveSuggestionItem" | "keyAccessor" | "theme"
> &
  Partial<Pick<GenericStaticTagPickerProps<TItem>, "onFilterItems">>;

/**
 * Extended TagPicker that accepts a function to resolve all items once.
 * See withStaticItems HOC for more information.
 *
 * The default search filtering used a case-insensitive startsWith algorithm.
 *
 * excludeSelectedItems is defaulted to false, which means already selected items will show
 * in the list (styled differently).
 *
 * @param props
 */
export const StaticMultiTagPicker: FunctionComponent<
  StaticMultiTagPickerProps
> = props => {
  const onFilterItems: ConvertToWithDataSourceProps<
    GenericMultiTagPickerProps<ITag>,
    ITag
  >["onFilterItems"] = (filter, context) => {
    if (props.onFilterItems) {
      return props.onFilterItems(filter, context);
    }
    return context.items.filter(tag =>
      tag.name.toUpperCase().startsWith(filter.trim().toUpperCase())
    );
  };

  return (
    <StaticExtendedPickerBase
      {...props}
      onResolveSuggestionItem={x => x}
      keyAccessor={x => x.key}
      onFilterItems={onFilterItems}
    />
  );
};
